import { Hub } from 'aws-amplify/utils';
import PropTypes from 'prop-types';
import { Component, useState } from 'react';
import { Helmet } from 'react-helmet';
import { Link, Redirect, Route } from 'react-router-dom';

import {
  STATE_SIGNEDIN,
  STATE_SIGNIN,
  handleRetrySignInUser,
  withAuthUser,
} from '../auth/AuthProvider';
import { getFriendlyAuthErrorMessage, getRedirUrl } from '../auth/utils';
import { Container, PrimaryButton, Section } from '../components/Base';
import { SimpleHeader } from '../components/Header';
import LoaderDots from '../components/LoaderDots';
import connectExtension from '../extension/Provider';
import { log } from '../logger';
import { getTitle } from '../utils/document';

class AuthPage extends Component {
  static propTypes = {
    match: PropTypes.object.isRequired, // from react router Route
    extensionGetHandshakes: PropTypes.func.isRequired, // from connectExtension
    authState: PropTypes.string.isRequired,
    authError: PropTypes.string,
  };

  constructor(props) {
    super(props);

    this.unsubscribeHub = Hub.listen('auth', this.onHubCapsule);
    this.state = {
      doRedirect: false,
      showMessage: false,
      explicitRedirect: undefined,
    };
    this.didCognitoHostedUI = true;
  }

  // life-cycle
  componentDidMount() {
    this._mounted = true;
    window.setTimeout(() => {
      if (this._mounted) {
        this.setState({ showMessage: true });
      }
    }, 3000);
  }

  componentWillUnmount() {
    this._mounted = false;
    this.unsubscribeHub();
  }

  // hub listener
  onHubCapsule = (capsule) => {
    const { channel, payload } = capsule;
    if (channel === 'auth') {
      log.group(`[AuthPage.onHubCapsule.auth] ${payload.event}`);
      log.debug('location.href', window.location.href);
      log.debug('channel', channel);
      log.groupCollapsed('payload');
      log.debug(log.j(payload));
      log.groupEnd();
      log.debug('props.authState', this.props.authState);
      log.debug('didCognitoHostedUI', this.didCognitoHostedUI);
      log.groupEnd();
      switch (payload.event) {
        case 'customOAuthState': {
          const redirectPath = new URLSearchParams(payload.data).get(
            'redirectPath',
          );
          if (redirectPath) {
            this.setState({ ...this.state, explicitRedirect: redirectPath });
          }
          break;
        }
        case 'signInWithRedirect': {
          // wait for this event to trigger the redirect
          if (this.props.authState !== STATE_SIGNEDIN) {
            // record in non-reactive state that we have witnessed this event
            // so once we login we can do the redirect
            // NOTE: we do not want to redirect to other components
            // in a "signIn" state, when we'll momentarily be "signedIn",
            // e.g., the login popup auto-redirects to the hosted UI
            // when logged out
            this.didCognitoHostedUI = true;
          } else {
            // already signed in, so redirect now
            this.setState({ doRedirect: true });
          }
          break;
        }
        default: {
          break;
        }
      }
    }
  };

  shouldDoRedirect() {
    return (
      this.state.doRedirect ||
      (this.didCognitoHostedUI && this.props.authState === STATE_SIGNEDIN)
    );
  }

  // render
  render() {
    const { match } = this.props;

    // NOTE - these match with or withhout trailing slash
    // since no `exact` params
    return (
      <>
        <Helmet>
          <title>{getTitle('Auth')}</title>
        </Helmet>
        <Route path={`${match.path}/signin`} render={this.renderSignIn} />
        <Route path={`${match.path}/signout`} render={this.renderSignOut} />
      </>
    );
  }

  renderSignIn = () => {
    const { showMessage, explicitRedirect } = this.state;
    const { authError, authState } = this.props;

    // wait for the proper Hub events for OAuth setup to complete
    const redirUrl = explicitRedirect ?? getRedirUrl();
    log(
      `[AuthPage.renderSignIn] redirUrl=${redirUrl}, token=${document.cookie
        .split('; ')
        .find((tok) => tok.startsWith(getRedirUrl.COOKIE_KEY + '='))}`,
    );
    if (this.shouldDoRedirect()) {
      return <Redirect to={redirUrl} />;
    } else {
      if (authError) {
        return (
          <AuthMessage buttonText="Try again">
            <p>{getFriendlyAuthErrorMessage(authError)}</p>
            <AuthErrorDetails authError={authError} />
          </AuthMessage>
        );
      } else if (authState === STATE_SIGNIN) {
        // settled as logged out with no hosted-UI error, e.g. navigated
        // directly to this page. NOTE: Amplify waits for any in-flight OAuth
        // code exchange before rejecting `currentAuthenticatedUser()` ("No
        // current user"), so we won't get here mid-sign-in
        return (
          <AuthMessage buttonText="Sign in">
            <p>You are not signed in.</p>
          </AuthMessage>
        );
      } else {
        return (
          <Section>
            <div className="container text-center">
              <br />
              <br />
              <LoaderDots className="inline-block" />
              <br />
              <br />
              <br />
              <br />
              {showMessage && (
                <span className="dim">
                  Updating logged-in state. Go to{' '}
                  <Link
                    to={'/'}
                    className="dim"
                    style={{ textDecoration: 'underline' }}
                  >
                    home
                  </Link>{' '}
                  if this is taking too long.
                </span>
              )}
            </div>
          </Section>
        );
      }
    }
  };

  renderSignOut = () => {
    // do the extension logout
    this.props
      .extensionGetHandshakes()
      .then((children) => {
        children.forEach((child) => {
          child.call('login', {}); // log the user out
        });
      })
      .catch((err) => {
        log.error('Unable to log out of extension', err);
      });

    // instantly redirect signOut urls
    const redirUrl = getRedirUrl();
    return <Redirect to={redirUrl} />;
  };
}

// ## Helpers

/**
 * Layout for a message on the sign in page, with a button to (re)start
 * the hosted UI sign in
 */
const AuthMessage = ({ buttonText, children }) => {
  return (
    <>
      <SimpleHeader />
      <Section>
        <Container className="ml-0 max-w-[680px] space-y-5 text-pretty text-left">
          {children}
          <p>
            <PrimaryButton
              className="btn-standout"
              size="small"
              onClick={handleRetrySignInUser}
            >
              {buttonText}
            </PrimaryButton>
          </p>
        </Container>
      </Section>
    </>
  );
};

AuthMessage.propTypes = {
  buttonText: PropTypes.string.isRequired,
  children: PropTypes.node,
};

/**
 * Collapsed "Show full error" toggle that reveals the raw hosted-UI
 * `error_description`, e.g. for users to screenshot for support
 */
const AuthErrorDetails = ({ authError }) => {
  const [isOpen, setIsOpen] = useState(false);

  if (!isOpen) {
    return (
      <p>
        <button type="button" className="sly" onClick={() => setIsOpen(true)}>
          ▸ Show full error
        </button>
      </p>
    );
  }

  return (
    <div>
      <p className="mb-2">Full error:</p>
      <div className="break-words rounded bg-gray-200 px-3 py-2 font-mono text-sm">
        {authError}
      </div>
    </div>
  );
};

AuthErrorDetails.propTypes = {
  authError: PropTypes.string.isRequired,
};

export default withAuthUser(connectExtension(AuthPage));
